import { CSSProperties, PointerEvent as ReactPointerEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAppData } from '@/app-data';
import { useDocumentTitle } from '@/utils/use-document-title';
import * as api from '@/features/backlog/api';
import { Item, Member } from '@/features/backlog/types';
import { useEpics, useEpicDropTarget, epicFocusMatches, focusedEpicForCreate } from '@/features/backlog/epics';
import { useStatuses, useStatusMeta } from '@/features/backlog/statuses';
import { priorityMeta, taskTypeMeta, hiddenFromBoard } from '@/features/backlog/constants';
import { useHistory } from '@/features/history/history';
import { snapshotCommand } from '@/features/history/commands';
import { useLiveItems } from '@/features/realtime/realtime';
import { useFilters } from '@/features/backlog/use-filters';
import { filtersActive, matchesFilters } from '@/features/backlog/filter';
import { fmtDate } from '@/features/backlog/format';
import { buildFlat, childCount, subtreeIds, visibleSelectedId } from '@/pages/board/tree';
import { Axis, ItemSpan, Zoom, ZOOMS, buildAxis, dayFromISO, fillSpan, isoFromDay, itemSpan, todayDay, zoomDef } from '@/pages/timeline/scale';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/components/ui/cn';
import { Popover, menuRow } from '@/components/ui/Popover';
import { ContextMenu, useContextMenu } from '@/components/ui/ContextMenu';
import { Calendar, Check, Chevron, ChevronDown, Comment, Plus, PriorityChevron, TaskTypeGlyph } from '@/components/ui/icons';
import MemberAvatar from '@/components/member-avatar';
import FilterBar from '@/components/filter-bar';
import ItemDrawer, { ItemTab } from '@/components/item-drawer';
import { useItemPanel } from '@/components/item-panel';
import { ancestorIds, prefersReducedMotion, useItemUrlSync } from '@/features/backlog/use-item-url';
import SavedViews from '@/components/saved-views';

// Frozen-grid dimensions. LEFT_W is the sticky record pane; HEADER_H = month tier (24) +
// tick tier (32); ROW_H matches the left cell so bars line up 1:1 with the record list.
const LEFT_W = 300;
const ROW_H = 44;
const HEADER_H = 56;
const INDENT = 16; // per depth level in the left tree
const BAR_H = 26;
const HANDLE = 8; // px-wide resize zone at each bar edge
const MIN_BAR = 14; // never render a bar narrower than this (single-day items stay clickable)


// A live bar drag: shifting the whole bar ('move') or dragging one edge ('l' / 'r').
type Drag = {
  id: string;
  // move/resize a bar, or 'create' = draw a new bar on an undated row's track.
  mode: 'move' | 'l' | 'r' | 'create';
  startX: number;
  px: number; // pxPerDay captured at grab time
  // The drag reference span. For move/resize it's the item's REAL span (never the synthetic
  // fill) so persistence stays honest; for 'create', s == e == the anchor day of the draw.
  span: ItemSpan;
  deltaDays: number;
  moved: boolean;
  cx: number; // live cursor position, for the drag tooltip
  cy: number;
};

const ZKEY = 'spark-board.timeline.zoom';
const loadZoom = (): Zoom => {
  const v = localStorage.getItem(ZKEY);
  return v === 'days' || v === 'weeks' || v === 'months' ? v : 'weeks';
};

export default function TimelinePage() {
  const { currentBoard, members, loading: appLoading, error: appError } = useAppData();
  useDocumentTitle(currentBoard?.name);
  const boardId = currentBoard?.id ?? null;
  const { filters, setFilters } = useFilters();
  const { record, dataVersion } = useHistory();
  // Reload items when the board's status SET changes (a delete reassigns items server-side).
  const { valuesKey } = useStatuses();

  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(false);
  // Which board's items are currently loaded — gates the auto-scroll so it fires only once the
  // NEW board's data is in (a plain `ready` flag re-scrolls with the previous board's axis).
  const [loadedBoardId, setLoadedBoardId] = useState<string | null>(null);
  const [zoom, setZoom] = useState<Zoom>(loadZoom);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  // App-wide details panel (see ItemPanelProvider): stays docked across view switches and gives
  // a single "selected item" the rows highlight.
  const panel = useItemPanel();
  const { focusedEpicId } = useEpics();
  // The epic a new item should adopt from the current focus (null when focused on nothing / "Unassigned").
  const focusEpicForNew = focusedEpicForCreate(focusedEpicId);
  const [drag, setDrag] = useState<Drag | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  // Right-click anywhere on the timeline surface to create an item (touch users use + New item).
  const timelineCm = useContextMenu();

  const reload = useCallback(async () => {
    if (!boardId) return;
    setLoading(true);
    try {
      const res = await api.listItems({
        limit: 2000,
        filter: [{ name: 'board_id', op: 'eq', value: boardId }],
        sort: [{ name: 'position', dir: 'asc' }],
      });
      setItems(res.data);
    } finally {
      setLoading(false);
      setLoadedBoardId(boardId);
    }
  }, [boardId]);

  // dataVersion bumps after an undo/redo/seek so the view reloads from server truth;
  // valuesKey changes when a status is added/removed (a delete moved items) → refetch.
  useEffect(() => {
    reload();
  }, [reload, dataVersion, valuesKey]);

  // Apply other people's edits live (create/update/move/delete/restore) as they happen.
  useLiveItems(boardId, setItems, reload);

  useEffect(() => {
    localStorage.setItem(ZKEY, zoom);
  }, [zoom]);

  const memberMap = useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);
  // Epics/initiatives are hidden from the timeline (they have their own UI — epics live in the left
  // rail), matching the Table and Kanban. Mutations below still use the full `items`.
  const boardItems = useMemo(() => items.filter((i) => !hiddenFromBoard(i)), [items]);
  const itemsById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const active = filtersActive(filters);
  // The record cell to ring for the open item: itself when visible, else the collapsed ancestor
  // it's folded inside. The filtered view is flat (no nesting), so it's a straight id there.
  const highlightId = active
    ? panel.selectedId
    : visibleSelectedId(panel.selectedId, itemsById, (cid) => collapsed.has(cid));

  // Rows: outline (depth-first) order in tree mode, honoring collapse; a flat matching list
  // when filtering — exactly like the Table, so the three views stay consistent.
  const rows = useMemo(() => {
    if (active) return boardItems.filter((it) => matchesFilters(it, filters, memberMap)).map((item) => ({ item, depth: 0 }));
    return buildFlat(boardItems, collapsed);
  }, [active, boardItems, filters, collapsed, memberMap]);

  // The item set the axis fits to (all in tree mode, the matching set when filtered) — kept
  // independent of collapse so folding a subtree never reshapes the time axis.
  const axisItems = useMemo(
    () => (active ? boardItems.filter((it) => matchesFilters(it, filters, memberMap)) : boardItems),
    [active, boardItems, filters, memberMap],
  );

  const today = todayDay();
  const axis = useMemo(() => {
    // Default window always fills the view, even with sparse data: 3 weeks back → 8 weeks
    // ahead of today. Items dated outside that window extend the axis (scroll to reach them).
    let min = today - 21;
    let max = today + 56;
    for (const it of axisItems) {
      const sp = itemSpan(it.start_date, it.end_date);
      if (!sp) continue;
      min = Math.min(min, sp.s);
      max = Math.max(max, sp.e);
    }
    return buildAxis(min, max, zoom);
  }, [axisItems, zoom, today]);

  const xOf = useCallback((day: number) => (day - axis.start) * axis.pxPerDay, [axis]);
  const todayX = xOf(today);
  const todayVisible = today >= axis.start && today < axis.end;

  // Mirror the open item into a shareable `?item=<key>` URL, and open + center an item a link arrives
  // on. `revealItem` un-collapses a deep-linked item's ancestors so its own row shows. Centering runs
  // through the Gantt's own scroll box: the row vertically, and the item's start (or end) date on the
  // horizontal axis — the default row-only scroll can't reach the date column.
  const revealItem = useCallback(
    (id: string) => {
      const anc = ancestorIds(id, itemsById);
      if (anc.size === 0) return;
      setCollapsed((prev) => {
        if (![...anc].some((a) => prev.has(a))) return prev;
        const next = new Set(prev);
        anc.forEach((a) => next.delete(a));
        return next;
      });
    },
    [itemsById],
  );
  const centerItem = useCallback(
    (id: string, el: HTMLElement) => {
      const box = scrollRef.current;
      if (!box) return;
      const cr = box.getBoundingClientRect();
      const rr = el.getBoundingClientRect();
      const top = Math.max(0, box.scrollTop + (rr.top - cr.top) - (box.clientHeight - rr.height) / 2);
      const it = itemsById.get(id);
      const day = dayFromISO(it?.start_date) ?? dayFromISO(it?.end_date);
      // Center the date within the VISIBLE track [LEFT_W, clientWidth] — the frozen record pane covers
      // [0, LEFT_W], so centering on the full box width would tuck the date behind it at narrow (docked)
      // widths. This lands it at viewport-x (LEFT_W + clientWidth) / 2 for any box width.
      const left = day != null ? Math.max(0, xOf(day) - (box.clientWidth - LEFT_W) / 2) : box.scrollLeft;
      box.scrollTo({ top, left, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    },
    [itemsById, xOf],
  );
  useItemUrlSync(items, { revealItem, scrollToItem: centerItem });

  // Center today (clamped into range) on first load, board change, and zoom change — but not
  // on date edits, so persisting a drag never yanks the user's scroll position.
  const scrollToDay = useCallback(
    (day: number, offset = 0) => {
      const el = scrollRef.current;
      if (!el) return;
      el.scrollLeft = Math.max(0, xOf(day) - offset);
    },
    [xOf],
  );
  // Default view opens 3 weeks before today (so today sits ~3 weeks in). Fires when the loaded
  // board changes (so it uses the NEW board's axis), on zoom change, and once the shell finishes
  // its initial load (so the scroll container exists) — but never on date edits, so a drag/save
  // never yanks scroll (a failed-save reload re-sets loadedBoardId to the same value = no-op).
  useEffect(() => {
    if (!loadedBoardId) return;
    requestAnimationFrame(() => scrollToDay(today - 21));
    // Intentionally excludes `axis`/`scrollToDay`/`today`: reposition only on these triggers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedBoardId, zoom, appLoading]);

  // ---- inline persistence (mirrors Table/Kanban saveField) ----
  const saveField = useCallback(
    async (item: Item, patch: Partial<Item>) => {
      const merged = { ...item, ...patch };
      setItems((cur) => cur.map((i) => (i.id === item.id ? merged : i)));
      try {
        const saved = await api.updateItem(item.id, {
          board_id: merged.board_id,
          parent_id: merged.parent_id,
          epic_id: merged.epic_id,
          title: merged.title,
          description: merged.description,
          status: merged.status,
          priority: merged.priority,
          type: merged.type,
          assignee_id: merged.assignee_id,
          pdm_id: merged.pdm_id,
          source: merged.source,
          start_date: merged.start_date,
          end_date: merged.end_date,
        });
        setItems((cur) => cur.map((i) => (i.id === item.id ? saved : i)));
        record(snapshotCommand(item, saved, `Dates ${item.key}`));
      } catch {
        reload();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reload, record],
  );

  // Link an item to an epic dropped from the sidebar, cascading to its whole subtree so every
  // nested subtask picks up the same epic. The subtree is computed from the freshest state inside
  // the updater; the server cascades the same set atomically. Narrow write — only epic_id changes.
  const linkEpic = useCallback(
    (itemId: string, epicId: string) => {
      if (itemId === epicId) return;
      setItems((cur) => {
        const ids = subtreeIds(cur, itemId);
        // Mirror the server cascade: (re)link the drop target itself, and cascade only to subtasks
        // that have NO epic yet — a subtask already on another epic is a deliberate choice we leave
        // alone — and never to a nested epic.
        return cur.map((i) =>
          ids.has(i.id) && i.id !== epicId && (i.id === itemId || (i.type !== 'epic' && !i.epic_id))
            ? { ...i, epic_id: epicId }
            : i,
        );
      });
      api.setItemEpic(itemId, epicId, { cascade: true }).catch(() => reload());
    },
    [reload],
  );

  const toggle = (id: string) =>
    setCollapsed((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  const openDrawer = (id: string, tab?: ItemTab) => panel.openItem(id, tab);

  // ---- bar drag (move / resize), tz-safe via day-index math ----
  const startDrag = (e: ReactPointerEvent, item: Item, mode: Drag['mode'], span: ItemSpan) => {
    if (e.button !== 0) return; // right/middle click never drags a bar (leaves the context menu free)
    e.preventDefault();
    e.stopPropagation();
    setDrag({ id: item.id, mode, startX: e.clientX, px: axis.pxPerDay, span, deltaDays: 0, moved: false, cx: e.clientX, cy: e.clientY });
  };

  // Draw a new bar on an undated row: the anchor day is wherever the pointer went down.
  const startCreate = (e: ReactPointerEvent, item: Item) => {
    if (e.button !== 0) return; // right/middle click never draws a bar (leaves the context menu free)
    e.preventDefault();
    const el = scrollRef.current;
    if (!el) return;
    const contentX = e.clientX - el.getBoundingClientRect().left + el.scrollLeft;
    const anchor = axis.start + Math.floor((contentX - LEFT_W) / axis.pxPerDay);
    const span: ItemSpan = { s: anchor, e: anchor, hasStart: false, hasEnd: false };
    setDrag({ id: item.id, mode: 'create', startX: e.clientX, px: axis.pxPerDay, span, deltaDays: 0, moved: false, cx: e.clientX, cy: e.clientY });
  };

  const dragRef = useRef<Drag | null>(null);
  dragRef.current = drag;
  const itemsRef = useRef(items);
  itemsRef.current = items;

  // Subscribe the window listeners once per drag (keyed on active, not on the drag object,
  // which changes every pointermove) — the handlers read fresh state from the refs above.
  const dragging = drag !== null;
  useEffect(() => {
    if (!dragging) return undefined;
    const onMove = (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const deltaDays = Math.round((e.clientX - d.startX) / d.px);
      const moved = d.moved || Math.abs(e.clientX - d.startX) > 3;
      if (deltaDays !== d.deltaDays || moved !== d.moved || e.clientX !== d.cx || e.clientY !== d.cy)
        setDrag({ ...d, deltaDays, moved, cx: e.clientX, cy: e.clientY });
    };
    const onUp = () => {
      const d = dragRef.current;
      setDrag(null);
      if (!d) return;
      const item = itemsRef.current.find((i) => i.id === d.id);
      if (!item) return;
      // A press that never moved is a click → open the item (move on a bar, or an undated track).
      if ((d.mode === 'move' || d.mode === 'create') && !d.moved) return openDrawer(d.id);
      if (d.deltaDays === 0 && d.mode !== 'create') return;

      // All math is on the item's REAL span (d.span), so the synthetic fill start is never
      // persisted; flags decide which fields a gesture may write.
      const { s, e, hasStart, hasEnd } = d.span;
      const patch: Partial<Item> = {};
      if (d.mode === 'create') {
        // Draw defines both dates from the swept range.
        patch.start_date = isoFromDay(Math.min(s, s + d.deltaDays));
        patch.end_date = isoFromDay(Math.max(s, s + d.deltaDays));
      } else if (d.mode === 'move') {
        if (hasStart) patch.start_date = isoFromDay(s + d.deltaDays);
        if (hasEnd) patch.end_date = isoFromDay(e + d.deltaDays);
      } else if (d.mode === 'l') {
        // Left handle only shown for items with a real start, so `s` is that real start.
        patch.start_date = isoFromDay(Math.min(s + d.deltaDays, e)); // never cross the end
      } else {
        const end = e + d.deltaDays;
        patch.end_date = isoFromDay(hasStart ? Math.max(end, s) : end); // floor at a real start only
      }
      if (Object.keys(patch).length) saveField(item, patch);
    };
    // A cancelled gesture (e.g. touch pan takeover) must NOT commit — just drop the drag.
    const onCancel = () => setDrag(null);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
    };
    // onMove/onUp read live state from refs; openDrawer/setDrag are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragging, saveField]);

  const recordCount = active ? rows.length : items.length;

  if (appLoading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }
  if (appError) return <div className="py-20 text-center text-danger">{appError}</div>;
  if (!currentBoard) return <div className="py-20 text-center text-fg-muted">No board selected.</div>;

  return (
    <div>
      <FilterBar
        filters={filters}
        onChange={setFilters}
        items={items}
        members={members}
        meta={`${recordCount} record${recordCount === 1 ? '' : 's'}`}
        trailing={
          <>
            {loading ? <span className="text-[12px] text-fg-faint">Saving…</span> : null}
            <SavedViews boardId={currentBoard.id} filters={filters} onApply={setFilters} />
          </>
        }
      />

      {/* `isolate` confines the sticky header/cell z-indices to this card's own stacking
          context, so they never paint over the FilterBar's popovers (Status/Assignee/Views). */}
      <div
        ref={scrollRef}
        onContextMenu={timelineCm.triggerProps.onContextMenu}
        className={cn('thin-scroll relative isolate overflow-auto rounded-card border border-line bg-surface', drag && 'select-none')}
        style={{ maxHeight: 'calc(100vh - 172px)' }}
      >
        <div className="relative" style={{ width: LEFT_W + axis.width, minWidth: '100%' }}>
          {/* Background grid: shaded columns + today line, spanning all rows behind the bars. */}
          <div className="pointer-events-none absolute z-0" style={{ top: HEADER_H, left: LEFT_W, right: 0, bottom: 0 }}>
            {axis.bands.map((b, i) =>
              b.shaded ? <div key={i} className="absolute top-0 bottom-0 bg-layer" style={{ left: b.x, width: b.w }} /> : null,
            )}
            {todayVisible ? (
              <div className="absolute top-0 bottom-0 border-l-2 border-dashed border-fg-faint/50" style={{ left: todayX }} />
            ) : null}
          </div>

          {/* ---- sticky header: corner controls + time axis ---- */}
          {/* border-b is on the container (not the children) so the hairline spans the full
              stretched width when the canvas is narrower than the viewport (Months zoom). */}
          <div className="sticky top-0 z-40 flex border-b border-line bg-surface" style={{ height: HEADER_H }}>
            <div
              className="sticky left-0 z-10 flex items-center gap-1.5 border-r border-line bg-surface px-3"
              style={{ width: LEFT_W, height: HEADER_H }}
            >
              <ZoomPicker zoom={zoom} onChange={setZoom} />
              <button
                type="button"
                onClick={() => scrollToDay(today - 21)}
                className="tap-target-y inline-flex h-7 items-center gap-1.5 rounded-full border border-line px-2.5 text-[12px] font-medium text-fg-muted transition-colors hover:bg-layer-strong hover:text-fg"
                title="Scroll to today"
              >
                <Calendar className="h-3.5 w-3.5" />
                Today
              </button>
              <button
                type="button"
                onClick={() => scrollToDay(axis.start)}
                className="tap-target inline-flex h-7 w-7 items-center justify-center rounded-full border border-line text-fg-muted transition-colors hover:bg-layer-strong hover:text-fg"
                title="Jump to start"
                aria-label="Jump to start"
              >
                <FitIcon />
              </button>
            </div>

            <div className="relative bg-surface" style={{ width: axis.width, height: HEADER_H }}>
              {/* top tier — month / year spans */}
              {axis.top.map((s, i) => (
                <div
                  key={i}
                  className="absolute top-0 flex items-center truncate border-r border-line px-2 text-[12px] font-semibold text-fg-strong"
                  style={{ left: s.x, width: s.w, height: 24 }}
                >
                  {s.label}
                </div>
              ))}
              {/* bottom tier — day / week / month ticks */}
              {axis.bottom.map((t, i) => (
                <div
                  key={i}
                  className={cn('absolute flex items-center pl-1.5 text-[11px] tnum', t.dim ? 'text-fg-ghost' : 'text-fg-faint')}
                  style={{ left: t.x, top: 24, height: HEADER_H - 24 }}
                >
                  {t.label}
                </div>
              ))}
              {/* Today marker label, anchored to the right of the line (not centered) so it
                  never pokes left into the side rail. No z-index: it stays BELOW the sticky
                  corner (z-10) so the corner clips it as today scrolls under the rail. */}
              {todayVisible ? (
                <div className="absolute flex items-center gap-1 whitespace-nowrap pl-1.5" style={{ left: todayX, top: 5 }}>
                  <span className="h-1.5 w-1.5 rounded-full bg-fg-faint" />
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-fg-faint">Today</span>
                </div>
              ) : null}
            </div>
          </div>

          {/* ---- rows ---- */}
          {rows.length === 0 ? (
            <div className="sticky left-0 px-6 py-16 text-center text-[13px] text-fg-faint" style={{ width: '100vw', maxWidth: LEFT_W + axis.width }}>
              {loading ? 'Loading…' : active ? 'No items match your filters.' : 'No items yet.'}
            </div>
          ) : (
            rows.map(({ item, depth }) => {
              const kids = active ? 0 : childCount(boardItems, item.id);
              return (
                <Row
                  key={item.id}
                  item={item}
                  depth={depth}
                  hasChildren={kids > 0}
                  collapsed={collapsed.has(item.id)}
                  highlightId={highlightId}
                  member={memberMap.get(item.assignee_id ?? '')}
                  axis={axis}
                  xOf={xOf}
                  drag={drag}
                  onToggle={() => toggle(item.id)}
                  onOpen={(tab) => openDrawer(item.id, tab)}
                  onStartDrag={startDrag}
                  onCreateStart={startCreate}
                  onLinkEpic={linkEpic}
                  focusedEpicId={focusedEpicId}
                />
              );
            })
          )}
        </div>
      </div>

      {/* Live tooltip near the cursor while dragging: the date(s) the gesture lands on. */}
      {drag && drag.moved ? (
        <div
          className="pointer-events-none fixed z-[100] rounded-md bg-fg px-2 py-1 text-[11px] font-semibold text-ink shadow-lg shadow-black/30"
          style={{ left: drag.cx + 14, top: drag.cy + 16 }}
        >
          {dragDateLabel(drag)}
        </div>
      ) : null}

      <ItemDrawer
        open={panel.open}
        itemId={panel.itemId}
        boardId={currentBoard.id}
        initialType={panel.initialType}
        initialEpicId={panel.initialEpicId ?? null}
        initialTab={panel.initialTab as ItemTab | undefined}
        items={items}
        onClose={panel.close}
        onSaved={reload}
      />

      {timelineCm.open ? (
        <ContextMenu
          open
          x={timelineCm.x}
          y={timelineCm.y}
          onClose={timelineCm.close}
          items={[{ label: 'New item', icon: <Plus />, onSelect: () => panel.openCreate({ initialEpicId: focusEpicForNew }) }]}
        />
      ) : null}
    </div>
  );
}

function Row(props: {
  item: Item;
  depth: number;
  hasChildren: boolean;
  collapsed: boolean;
  member?: Member;
  axis: Axis;
  xOf: (day: number) => number;
  drag: Drag | null;
  onToggle: () => void;
  /** Open the item's drawer, optionally deep-linked to a tab (e.g. 'comments'). */
  onOpen: (tab?: ItemTab) => void;
  onStartDrag: (e: ReactPointerEvent, item: Item, mode: Drag['mode'], span: ItemSpan) => void;
  onCreateStart: (e: ReactPointerEvent, item: Item) => void;
  /** The item id to ring as "open in the details panel" — already folded to a visible ancestor. */
  highlightId: string | null;
  /** Link this item to an epic dragged from the sidebar (native drop on the record cell). */
  onLinkEpic: (itemId: string, epicId: string) => void;
  /** The epic being highlighted from the sidebar, so unlinked rows dim. */
  focusedEpicId: string | null;
}) {
  const { item, depth, hasChildren, collapsed, member, axis, xOf, drag, onToggle, onOpen, onStartDrag, onCreateStart } = props;
  const statusMeta = useStatusMeta();
  const sm = statusMeta(item.status);
  const pm = priorityMeta(item.priority);
  const tm = taskTypeMeta(item.type);
  const epicDrop = useEpicDropTarget(item.id, props.onLinkEpic);
  const focusDim = props.focusedEpicId != null && !epicFocusMatches(item.epic_id, props.focusedEpicId);
  const span = itemSpan(item.start_date, item.end_date);
  // Ring the record cell while its item is the one open in the details panel — the sticky cell
  // is already positioned, so the `.item-selected` ::after anchors to it without extra classes.
  const isSelected = props.highlightId === item.id;

  // While drawing a new bar on this (undated) row, preview it as a ghost from the swept range.
  const creating = drag?.mode === 'create' && drag.id === item.id;
  const gs = creating ? Math.min(drag.span.s, drag.span.s + drag.deltaDays) : 0;
  const ge = creating ? Math.max(drag.span.s, drag.span.s + drag.deltaDays) : 0;

  return (
    // `data-item-id` is the scroll target a shared `?item=` link centers on (see useItemUrlSync).
    <div className="group/row flex" style={{ height: ROW_H }} data-item-id={item.id}>
      {/* left: sticky record cell (also the epic-link drop target) */}
      <div
        {...epicDrop.dropProps}
        className={cn(
          'sticky left-0 z-30 flex items-center gap-1.5 border-b border-r border-line bg-surface pr-3 transition-colors group-hover/row:bg-surface-hover',
          // The sticky cell is already positioned, so the `.item-selected` ::after anchors to it.
          isSelected && 'item-selected',
          // Not linked to the focused epic — recede so the linked rows stand out.
          focusDim && 'opacity-45',
        )}
        style={{ width: LEFT_W, paddingLeft: 8 + depth * INDENT, ...(epicDrop.isOver ? { boxShadow: `inset 0 0 0 2px ${taskTypeMeta('epic').color}` } : {}) }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={onToggle}
            className="tap-target-y flex h-5 w-5 shrink-0 items-center justify-center rounded text-fg-faint transition-colors hover:bg-layer hover:text-fg"
            aria-label={collapsed ? 'Expand' : 'Collapse'}
          >
            <Chevron className={cn('h-3.5 w-3.5 transition-transform', !collapsed && 'rotate-90')} />
          </button>
        ) : (
          <span className="w-5 shrink-0" />
        )}
        <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: sm.color }} title={sm.label} />
        <span className="flex h-4 w-4 shrink-0 items-center justify-center" title={`Type: ${tm.label}`}>
          <TaskTypeGlyph type={item.type} color={tm.color} />
        </span>
        <button
          type="button"
          onClick={() => onOpen()}
          className="tap-target-y min-w-0 flex-1 truncate rounded px-1 py-0.5 text-left text-[13px] text-fg-soft transition-colors hover:text-fg"
          title={item.title}
        >
          {item.title}
        </button>
        {/* Comments — fades in on row hover; the fixed-width slot keeps the title from reflowing. */}
        <button
          type="button"
          onClick={() => onOpen('comments')}
          className="relative flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-fg-ghost opacity-0 transition-opacity hover:bg-layer-strong hover:text-accent focus-visible:opacity-100 group-hover/row:opacity-100"
          aria-label="Open comments"
          title="Comments"
        >
          <span className="relative inline-flex">
            <Comment className="h-4 w-4" />
            {/* Dot only when the item actually has comments — an at-a-glance "has discussion" cue. */}
            {(item.comment_count ?? 0) > 0 ? (
              <span className="absolute -right-1 -top-1 h-1.5 w-1.5 rounded-full bg-accent" />
            ) : null}
          </span>
        </button>
        <MemberAvatar member={member} size={24} className="shrink-0" />
        <span
          className="flex h-4 w-4 shrink-0 items-center justify-center"
          title={`Priority: ${pm.label}`}
          aria-label={`Priority: ${pm.label}`}
        >
          <PriorityChevron level={pm.level} color={pm.color} />
        </span>
      </div>

      {/* right: bar track */}
      <div className="relative" style={{ width: axis.width }}>
        {span ? (
          <Bar item={item} span={span} color={sm.color} axis={axis} xOf={xOf} drag={drag} onStartDrag={onStartDrag} />
        ) : (
          // Undated: the whole track is a draw surface — drag to define start/end, click to open.
          <div className="absolute inset-0 cursor-crosshair" onPointerDown={(ev) => onCreateStart(ev, item)} title="Drag to set dates">
            {creating ? (
              <div
                className="pointer-events-none absolute top-1/2 -translate-y-1/2 rounded-md border-2 border-dashed border-accent/70 bg-accent/15"
                style={{ left: xOf(gs), width: Math.max(MIN_BAR, (ge - gs + 1) * axis.pxPerDay), height: BAR_H }}
              />
            ) : (
              <span
                className="pointer-events-none absolute top-1/2 flex h-6 -translate-y-1/2 items-center rounded-md bg-layer px-2 text-[11px] text-fg-ghost opacity-0 transition-opacity group-hover/row:opacity-100"
                style={{ left: Math.max(0, xOf(todayDay()) - 8) }}
              >
                Drag to set dates
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Bar(props: {
  item: Item;
  span: ItemSpan;
  color: string;
  axis: Axis;
  xOf: (day: number) => number;
  drag: Drag | null;
  onStartDrag: (e: ReactPointerEvent, item: Item, mode: Drag['mode'], span: ItemSpan) => void;
}) {
  const { item, span, color, xOf, drag, onStartDrag } = props;

  // End-only items render as a filled "due by" bar via a synthetic start. vspan drives ONLY the
  // visuals (left/width); the drag reference passed to onStartDrag is the item's REAL `span`, so
  // a move/resize never persists the synthetic start (see the review-confirmed fix).
  const vspan = fillSpan(span, props.axis.start);

  // Apply the live drag transform to this bar's own (visual) span so it tracks the cursor.
  let { s, e } = vspan;
  const active = drag?.id === item.id;
  if (active && drag) {
    if (drag.mode === 'move') {
      s += drag.deltaDays;
      e += drag.deltaDays;
    } else if (drag.mode === 'l') {
      s = Math.min(vspan.s + drag.deltaDays, vspan.e);
    } else if (drag.mode === 'r') {
      e = Math.max(vspan.e + drag.deltaDays, vspan.s);
    }
  }

  const left = xOf(s);
  const width = Math.max(MIN_BAR, (e - s + 1) * props.axis.pxPerDay);
  const dateLabel =
    vspan.hasStart && vspan.hasEnd
      ? `${fmtDate(isoFromDay(s))} – ${fmtDate(isoFromDay(e))}`
      : vspan.hasStart
        ? `Starts ${fmtDate(isoFromDay(s))}`
        : `Due ${fmtDate(isoFromDay(e))}`;
  const title = `${item.key} · ${item.title}\n${dateLabel}`;

  const style: CSSProperties = {
    left,
    width,
    height: BAR_H,
    backgroundColor: rgba(color, 0.16),
    borderColor: rgba(color, 0.5),
  };

  return (
    // No overflow-hidden: it would make this bar the sticky title's scroll reference and break
    // the pinning. The title clips itself (truncate); the bar's padding keeps content off the corners.
    <div
      className={cn(
        'tl-bar absolute top-1/2 flex -translate-y-1/2 items-center rounded-md border px-2 shadow-sm shadow-black/10',
        active ? 'z-20 ring-1 ring-accent/60' : 'z-10',
      )}
      style={style}
      title={title}
      onPointerDown={(ev) => onStartDrag(ev, item, 'move', span)}
    >
      {/* Left resize handle — only when a real start exists. An end-only "due" bar has no real
          start to drag (dragging its synthetic left edge would fabricate a date); add one via the drawer. */}
      {span.hasStart ? (
        <span
          className="tl-handle absolute left-0 top-0 z-10 h-full"
          style={{ width: HANDLE }}
          onPointerDown={(ev) => onStartDrag(ev, item, 'l', span)}
        />
      ) : null}
      {/* solid status cap */}
      <span className="absolute left-0 top-0 h-full w-1 rounded-l-md" style={{ background: color }} />
      {/* Sticky title: pinned to the bar's visible-left edge as it scrolls (so a bar starting
          off-screen still shows its title), confined by sticky to stop at the bar's right border,
          ellipsis when the bar is too short. `left:LEFT_W` = the canvas's visible-left. */}
      <span
        className="pointer-events-none sticky z-[1] min-w-0 max-w-full truncate pl-1 text-[11px] font-medium text-fg"
        style={{ left: LEFT_W }}
      >
        {item.title}
      </span>
      {/* right resize handle */}
      <span
        className="tl-handle absolute right-0 top-0 z-10 h-full"
        style={{ width: HANDLE }}
        onPointerDown={(ev) => onStartDrag(ev, item, 'r', span)}
      />
    </div>
  );
}

function ZoomPicker({ zoom, onChange }: { zoom: Zoom; onChange: (z: Zoom) => void }) {
  const current = zoomDef(zoom);
  return (
    <Popover
      trigger={({ open, toggle }) => (
        <button
          type="button"
          onClick={toggle}
          aria-haspopup="menu"
          aria-expanded={open}
          className="tap-target-y inline-flex h-7 items-center gap-1.5 rounded-full border border-line bg-surface-inset px-2.5 text-[12px] font-medium text-fg transition-colors hover:bg-layer-strong"
        >
          {current.label}
          <ChevronDown open={open} className="text-fg-faint" />
        </button>
      )}
    >
      {(close) =>
        ZOOMS.map((z) => (
          <button
            key={z.id}
            type="button"
            onClick={() => {
              onChange(z.id);
              close();
            }}
            className={cn(menuRow, 'min-w-[120px]', z.id === zoom ? 'text-fg' : 'text-fg-soft')}
          >
            <span className="flex-1">{z.label}</span>
            {z.id === zoom ? <Check className="text-accent" /> : null}
          </button>
        ))
      }
    </Popover>
  );
}

/** Expand-to-fit / jump-to-start glyph (two arrows meeting a center bar). */
function FitIcon() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 3L3 3 3 6M10 3l3 0 0 3M6 13H3v-3M10 13h3v-3" />
    </svg>
  );
}

/** The date(s) a live drag lands on, for the cursor tooltip. Uses the REAL span (d.span). */
function dragDateLabel(d: Drag): string {
  const { s, e, hasStart } = d.span;
  const delta = d.deltaDays;
  if (d.mode === 'create') return `${fmtDate(isoFromDay(Math.min(s, s + delta)))} – ${fmtDate(isoFromDay(Math.max(s, s + delta)))}`;
  if (d.mode === 'l') return fmtDate(isoFromDay(Math.min(s + delta, e)));
  if (d.mode === 'r') {
    const end = e + delta;
    return fmtDate(isoFromDay(hasStart ? Math.max(end, s) : end));
  }
  // move
  if (d.span.hasStart && d.span.hasEnd) return `${fmtDate(isoFromDay(s + delta))} – ${fmtDate(isoFromDay(e + delta))}`;
  if (d.span.hasEnd) return `Due ${fmtDate(isoFromDay(e + delta))}`;
  return `Starts ${fmtDate(isoFromDay(s + delta))}`;
}

/** #rrggbb → rgba() string at the given alpha (status colors are all hex). */
function rgba(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  const n = parseInt(h.length === 3 ? h.replace(/(.)/g, '$1$1') : h, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}
